import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import img from "@/assets/tv-mounting.jpg";
import { serviceAreas } from "@/data/serviceAreas";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import type { Faq } from "@/data/serviceFaqs";

const faqs: Faq[] = [
  {
    q: "How much does TV mounting cost in Denver?",
    a: "Standard wall mounts on drywall start at $199 including the bracket, leveling, and basic cable management. Fireplace installs, brick or stone walls, and in-wall power relocation typically run $399–$799 depending on the wall type and how clean you want the cables. We give a fixed quote before we start.",
  },
  {
    q: "Can you mount a TV over my fireplace?",
    a: "Yes. We measure the heat envelope, plan the viewing angle (we usually recommend a tilt or pull-down mount above a fireplace), and run power and HDMI inside the wall so nothing is visible. We've done dozens of fireplace installs across Denver and the Front Range.",
  },
  {
    q: "Will you hide the cables inside the wall?",
    a: "Always. We use code-compliant in-wall power kits (no extension cords inside walls) and recessed media boxes for HDMI, fiber, and network. The only thing you'll see is the TV.",
  },
  {
    q: "Can you mount on brick, stone, or tile?",
    a: "Yes. We carry the right anchors and masonry bits for brick, stone, tile, plaster, and shiplap. The quote includes any specialty hardware so there are no surprises.",
  },
  {
    q: "Do you serve all of the Denver Metro Area?",
    a: "Yes — Denver, Aurora, Cherry Creek, Highlands Ranch, Castle Rock, Castle Pines, Boulder, Parker, Littleton, Lakewood, and the rest of the Front Range. Same-week appointments are usually available.",
  },
];

const bullets = [
  "Flush, tilt, full-motion, and in-wall recessed mounts",
  "In-wall power relocation and HDMI / fiber concealment",
  "Mounting on drywall, brick, stone, tile, and shiplap",
  "Above-fireplace installs with heat and viewing-angle planning",
  "Soundbar and center-channel speaker integration",
  "Perfectly leveled, anchored to studs or properly engineered backing",
];

const testimonials = [
  { quote: "Crew showed up on time, mounted a 75\" over our fireplace and ran every cable inside the wall. You can't tell anything's there. Worth every penny.", name: "Megan R.", location: "Cherry Creek" },
  { quote: "Got quoted half what Best Buy wanted, and the install is genuinely cleaner. Already booked them for the basement.", name: "Dan K.", location: "Highlands Ranch" },
  { quote: "Mounted three TVs in one visit including a tricky brick wall. Perfectly level, zero visible cables, soundbar wired in.", name: "Priya S.", location: "Wash Park, Denver" },
];

export const Route = createFileRoute("/services/tv-mounting")({
  head: () => ({
    meta: [
      { title: "TV Mounting Denver | Professional TV Installation | Apex AV" },
      {
        name: "description",
        content:
          "Professional TV mounting in Denver from $199 — hidden cables, in-wall power & perfectly level installs on drywall, brick or stone. Book: (303) 317-3233.",
      },
      { name: "keywords", content: "TV mounting Denver, professional TV installation Denver, fireplace TV mounting Denver, hidden cable TV mounting Denver, full-motion TV mount Denver, flush TV mount Denver, soundbar installation Denver, in-wall power kit Denver, large TV mounting Denver, brick TV mounting Denver, stone fireplace TV mount Denver" },
      { property: "og:title", content: "Professional TV Mounting — Apex AV Denver" },
      { property: "og:description", content: "Perfectly level TVs with hidden cables across the Denver Metro Area. From $199 — book your install today." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/tv-mounting" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Professional TV Mounting — Apex AV Denver" },
      { name: "twitter:description", content: "Perfectly level TVs with hidden cables across the Denver Metro Area. From $199 — book your install today." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/tv-mounting" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "tv-mounting",
            name: "TV Mounting",
            description: "Flush, tilt, and full-motion TV mounts with hidden cables in Denver. Starting at $199.",
            pageTitle: "TV Mounting Denver | Professional TV Installation | Apex AV",
            pageDescription:
              "Professional TV mounting in Denver from $199. Flush, tilt, full-motion, and fireplace mounts with hidden cables, in-wall power, and perfectly level installs on drywall, brick, stone, and tile.",
            image: img,
            faqs,
            serviceType: "TV Mounting",
            breadcrumbName: "TV Mounting",
            minPrice: 199,
            priceDescription: "Standard wall mount on drywall, includes bracket and basic cable management. Fireplace, brick, and in-wall power installs typically run $399–$799 with a fixed quote up front.",
            offerCatalog: bullets,
          }),
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      topics={["tv-mounting", "av-installation", "home-theater", "whole-home-audio"]}
      eyebrow="TV Mounting"
      title="TV Mounting in Denver"
      intro={<>From a single bedroom TV to a 98-inch statement display, our <strong>TV mounting in Denver</strong> covers fireplace TV mounting, hidden cables, soundbar integration, and perfectly calibrated screens that belong to the architecture.</>}
      image={img}
      imageAlt="Professional flush TV mounting with hidden cables in a Centennial Denver luxury living room"
      brands={["sanus", "sony", "lg", "samsung"]}
      bullets={bullets}
      features={[
        { title: "Zero visible cables", desc: "Code-compliant in-wall power kits and recessed media boxes — nothing dangling, ever." },
        { title: "Any wall, done right", desc: "Brick, stone, tile, plaster, shiplap — we have the anchors and experience for all of it." },
        { title: "Calibrated on the way out", desc: "Picture mode, motion smoothing, and sources dialed in before we leave the room." },
      ]}
      startingPrice="$199"
      priceNote="Standard drywall mount with bracket and cable management. Fireplace, brick, and in-wall power installs typically run $399–$799 with a fixed quote up front."
      testimonials={testimonials}
      faqs={faqs}
      serviceAreas={serviceAreas.map((a) => ({ slug: a.slug, name: a.name }))}
    />
  ),
});
